/**
 * メアドをチップ形式で複数入力する共通コンポーネント。
 * 配信予約ダイアログの CC / キャンペーンのメール配信カードの CC 入力などで再利用する。
 *
 * - Enter / カンマ / 改行 / セミコロン / 空白 区切りで確定
 * - 形式検証（不正なメアドは toast で警告し追加しない）
 * - 重複排除・小文字化
 * - blur 時に未確定の入力も取り込む（確定し忘れ対策）
 */
import { X } from 'lucide-react';
import { useState } from 'react';

import { toast } from '@/components/ui/toast';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

export function EmailChipsInput({ value, onChange, placeholder }: Props) {
  const [text, setText] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(/[,、;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
    const valid = parts.filter((p) => EMAIL_RE.test(p));
    const invalid = parts.filter((p) => !EMAIL_RE.test(p));
    if (invalid.length > 0) {
      toast.error(`不正なメアド: ${invalid.join(', ')}`);
    }
    if (valid.length === 0) return;
    const next = [...value];
    for (const v of valid) if (!next.includes(v)) next.push(v);
    onChange(next);
    setText('');
  };

  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
      {value.map((a) => (
        <span
          key={a}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
        >
          {a}
          <button
            type="button"
            onClick={() => onChange(value.filter((x) => x !== a))}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        type="email"
        placeholder={
          value.length === 0
            ? (placeholder ?? 'メアドを入力（Enter / カンマ で確定）')
            : ''
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(text);
          } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => {
          if (text.trim()) commit(text);
        }}
        className="flex-1 min-w-[20ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] font-mono"
      />
    </div>
  );
}
